import { logger } from '@/shared/utils/logger';

/**
 * Minimal `Storage`-compatible surface used by the application.
 */
export interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  /**
   * Like `setItem`, but reports whether the browser store accepted the write.
   * `false` means only the in-memory fallback holds the value (store missing,
   * denied, or e.g. `QuotaExceededError`).
   */
  trySetItem(key: string, value: string): boolean;
  removeItem(key: string): void;
  clear(): void;
  /** True when writes actually reach the browser store (not the memory fallback). */
  readonly persistent: boolean;
}

type StorageKind = 'localStorage' | 'sessionStorage';

function createMemoryStorage(): Map<string, string> {
  return new Map<string, string>();
}

/**
 * Wrap a browser `Storage` so every access is guarded. In private browsing,
 * with third-party storage blocked or inside sandboxed frames, merely touching
 * `window.localStorage` throws a `SecurityError`; the previous unguarded reads
 * left the app with an empty `#root` (A16). Reads/writes that fail fall back to
 * an in-memory map for the lifetime of the page so preferences still work
 * within the session, and the failure is logged once per store.
 */
export function createSafeStorage(kind: StorageKind): SafeStorage {
  const memory = createMemoryStorage();
  let warned = false;
  const warnOnce = (error: unknown) => {
    if (warned) return;
    warned = true;
    logger.warn(`[storage] ${kind} is unavailable; falling back to in-memory storage.`, error);
  };

  const resolve = (): Storage | null => {
    try {
      if (typeof window === 'undefined') return null;
      // Accessing the property itself can throw (SecurityError).
      const store = window[kind];
      if (!store) return null;
      return store;
    } catch (error) {
      warnOnce(error);
      return null;
    }
  };

  let persistent = false;
  const probe = resolve();
  if (probe) {
    try {
      const probeKey = `__dt_storage_probe__${Math.random().toString(36).slice(2)}`;
      probe.setItem(probeKey, '1');
      probe.removeItem(probeKey);
      persistent = true;
    } catch (error) {
      warnOnce(error);
    }
  }

  return {
    get persistent() {
      return persistent;
    },
    getItem(key) {
      const store = resolve();
      if (store) {
        try {
          return store.getItem(key);
        } catch (error) {
          warnOnce(error);
        }
      }
      return memory.has(key) ? memory.get(key)! : null;
    },
    setItem(key, value) {
      this.trySetItem(key, value);
    },
    trySetItem(key, value) {
      memory.set(key, value);
      const store = resolve();
      if (!store) return false;
      try {
        store.setItem(key, value);
        return true;
      } catch (error) {
        warnOnce(error);
        return false;
      }
    },
    removeItem(key) {
      memory.delete(key);
      const store = resolve();
      if (!store) return;
      try {
        store.removeItem(key);
      } catch (error) {
        warnOnce(error);
      }
    },
    clear() {
      memory.clear();
      const store = resolve();
      if (!store) return;
      try {
        store.clear();
      } catch (error) {
        warnOnce(error);
      }
    },
  };
}

export const safeLocalStorage: SafeStorage = createSafeStorage('localStorage');
export const safeSessionStorage: SafeStorage = createSafeStorage('sessionStorage');

/**
 * Parse a JSON value from storage, returning `fallback` for missing, malformed
 * or wrongly-shaped entries (validated by `isValid`).
 */
export function readJson<T>(storage: SafeStorage, key: string, fallback: T, isValid: (value: unknown) => value is T): T {
  const raw = storage.getItem(key);
  if (raw === null) return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}
